import { Button, Typography } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { resetBookingData, selectBookingData } from '../../../redux/slices/booking';
import { PHN_DOCTORS_ROUTE } from '../../../constants/routes';
import {
  Container,
  MainSection,
  DetailsGrid,
  DetailsItem,
  DetailsLabel,
  DetailsValue,
  GuideSection,
  ContactSection,
  ContactItem,
  ButtonSection,
  Footer,
  FooterLogo,
  FooterText,
} from './rhcStyles';
import { useAppDispatch } from '../../../hooks/redux-hooks';

const { Title, Text, Link } = Typography;

/**
 * RHC Thanks Page component
 * Displays appointment confirmation details after successful booking
 */
export const RhcThanksPage: FC = () => {
  const bookingData = useSelector(selectBookingData);
  const navigate = useNavigate();
  const { search } = useLocation();
  const dispatch = useAppDispatch();

  // Extract appointment details from booking data
  const appointmentDate = bookingData.appointment?.date ?? '';
  const appointmentTime = bookingData.appointment?.startTime ?? '';
  const clinicianName = `${bookingData.appointment?.specalistFirtName} ${bookingData.appointment?.specalistLastName}`;
  const centreName = bookingData.metadata?.centreName;

  // Format the date and time display
  const formatDateTime = (date: string, time: string): string => {
    // Convert YYYY-MM-DD to DD/MM/YYYY and time to display format
    const [year, month, day] = date.split('-');
    return `${day}/${month}/${year}T${time} +11:00`;
  };

  const handleBookAnother = () => {
    dispatch(resetBookingData());
    navigate(`${PHN_DOCTORS_ROUTE}${search}`);
  };

  return (
    <Container>

      <MainSection>
        <Title
          level={2}
          style={{
            fontSize: '32px',
            fontWeight: 400,
            color: '#000',
            marginBottom: '40px',
            lineHeight: 1.4,
          }}
        >
          Thank you for confirming your appointment with
          <br />
          Centralised Psychiatry Service
        </Title>

        <Title
          level={3}
          style={{
            fontSize: '24px',
            fontWeight: 600,
            color: '#000',
            marginBottom: '30px',
          }}
        >
          Appointment Details
        </Title>

        <DetailsGrid>
          <DetailsItem>
            <DetailsLabel>Date and Time:</DetailsLabel>
            <DetailsValue>{formatDateTime(appointmentDate, appointmentTime)}</DetailsValue>
          </DetailsItem>
          <DetailsItem>
            <DetailsLabel>Clinician:</DetailsLabel>
            <DetailsValue>{clinicianName}</DetailsValue>
          </DetailsItem>
          <DetailsItem>
            <DetailsLabel>Centre:</DetailsLabel>
            <DetailsValue>{centreName}</DetailsValue>
          </DetailsItem>
        </DetailsGrid>

        <GuideSection>
          <Title
            level={4}
            style={{
              fontSize: '20px',
              fontWeight: 600,
              color: '#000',
              marginBottom: '20px',
            }}
          >
            What to expect on the day of your appointment
          </Title>
          <Link
            href="https://example.com/appointment-guide"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: '16px',
              textDecoration: 'underline',
            }}
          >
            Centralised Psychiatry Service | Appointment Guide
          </Link>
        </GuideSection>

        <ContactSection>
          <Title
            level={4}
            style={{
              fontSize: '24px',
              fontWeight: 600,
              color: '#000',
              marginBottom: '30px',
            }}
          >
            Contact Us
          </Title>

          <ContactItem>
            <MailOutlined style={{ fontSize: '18px', marginRight: '10px' }} />
            <Text style={{ fontSize: '16px' }}>Email: rhc@telecare.com.au</Text>
          </ContactItem>
        </ContactSection>

        <ButtonSection>
          <Button
            type="primary"
            size="large"
            onClick={handleBookAnother}
            style={{
              borderRadius: '6px',
              padding: '12px 48px',
              height: 'auto',
              fontSize: '16px',
              fontWeight: 500,
              minWidth: '300px',
            }}
          >
            Book Another Appointment
          </Button>
        </ButtonSection>
      </MainSection>

      <Footer>
        <a href="https://www.telecare.com.au/" target="_blank" rel="noopener noreferrer">
          <FooterLogo src="/telecare-logo.svg" alt="Telecare Logo" />
        </a>
        <FooterText>Service powered by Telecare</FooterText>
      </Footer>
    </Container>
  );
};
